import { similarNames, type Tag } from '@anytracker/core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { Button, IconButton } from '../../components/Button.js'
import { EmptyState } from '../../components/EmptyState.js'
import { Select } from '../../components/Field.js'
import { Sheet } from '../../components/Sheet.js'
import { useToast } from '../../components/Toast.js'
import { useServices } from '../../services/ServicesProvider.js'
import styles from './Tags.module.css'

/** "On 3 entries", or that it is not used yet. */
function usageLabel(used: number): string {
  if (used === 0) {
    return 'Not used yet'
  }
  return used === 1 ? 'On 1 entry' : `On ${String(used)} entries`
}

/**
 * Tag management (T-306, refitted in T-908 and T-913).
 *
 * Unglamorous and used constantly. Free-text tagging produces near-duplicates
 * within weeks — "snack" and "snacks" — and without merge the only remedy is
 * re-tagging entries by hand, which nobody does, so the tags stop being useful
 * and the filtering built on them stops being trustworthy.
 *
 * **Deleting a tag detaches it; it never deletes an entry.** That is the rule
 * to be most careful with here, because the intuitive implementation of "delete
 * a tag" in a naive data model cascades — and losing a record because a label
 * was tidied up would be absurd.
 */
export function TagsScreen() {
  const { repos } = useServices()
  const { show } = useToast()
  const navigate = useNavigate()

  const [tags, setTags] = useState<Tag[] | null>(null)
  const [counts, setCounts] = useState<Map<string, number>>(new Map())
  const [merging, setMerging] = useState<Tag | null>(null)
  const [mergeInto, setMergeInto] = useState('')

  const reload = useCallback(async () => {
    const [all, usage] = await Promise.all([repos.tags.list(), repos.tags.usageCounts()])
    setTags(all)
    setCounts(usage)
  }, [repos])

  useEffect(() => {
    void reload()
  }, [reload])

  /*
   * Advisory only.
   *
   * "Travel" and "Travelling" might be the same thing or might be a deliberate
   * distinction, and the app has no way to know. Offering the merge is helpful;
   * performing it is not — so this only ever renders a sentence.
   */
  const duplicates = useMemo(
    () => similarNames((tags ?? []).map((tag) => tag.name)),
    [tags],
  )

  async function rename(tag: Tag, name: string) {
    if (name.trim() === '' || name.trim() === tag.name) {
      return
    }
    const result = await repos.tags.rename(tag.id, name.trim())
    if (!result.ok) {
      show({ message: result.errors[0]?.message ?? 'Could not rename.', tone: 'danger' })
      return
    }
    // Entries reference the tag by id, so nothing about their records changed.
    await reload()
  }

  async function recolour(tag: Tag, colour: string) {
    await repos.tags.update(tag.id, { colour })
    await reload()
  }

  async function remove(tag: Tag) {
    const result = await repos.tags.remove(tag.id)
    await reload()
    if (!result.ok) {
      show({ message: 'Could not delete that.', tone: 'danger' })
      return
    }
    // Detached, not cascaded: losing an entry because a label was tidied up
    // would be absurd (T-107).
    show({
      message:
        result.value.detachedFrom === 0
          ? `Deleted ${tag.name}.`
          : `Deleted ${tag.name}. It was removed from ${String(result.value.detachedFrom)} entries, which are otherwise unchanged.`,
    })
  }

  /**
   * The timeline, filtered to one tag, over **all** of history.
   *
   * The range is widened deliberately. The default window is thirty days, and
   * most tags anybody cares enough to tap are ones they applied to something
   * notable — which is exactly the kind of thing that happened months ago.
   * Landing on "nothing matches" after tapping a tag that says "On 12 entries"
   * is a plain contradiction.
   */
  function showEntries(tag: Tag) {
    void navigate(`/timeline?tag=${tag.id}&range=allTime`)
  }

  async function confirmMerge() {
    if (!merging || mergeInto === '') {
      return
    }
    const target = tags?.find((tag) => tag.id === mergeInto)
    const result = await repos.tags.merge(merging.id, mergeInto)
    setMerging(null)
    setMergeInto('')
    await reload()
    if (!result.ok) {
      show({ message: 'Could not merge those.', tone: 'danger' })
      return
    }
    show({
      message: `Moved ${String(result.value.moved)} entries to ${target?.name ?? 'the other tag'}.`,
    })
  }

  if (tags === null) {
    return (
      <>
        <h1>Tags</h1>
        <p>Loading…</p>
      </>
    )
  }

  const mergeTarget = tags.find((tag) => tag.id === mergeInto)

  return (
    <>
      {/*
        "New tag" beside the heading, matching Tracks and Check-ins (T-917).

        Tags are still normally made while logging, and the empty state below
        still says so. This is the other door, and it matters because the only
        other one is closed for anyone who has no tracks yet — and because this
        screen deletes.
      */}
      <div className={styles['header']}>
        <h1>Tags</h1>
        <Button variant="primary" onClick={() => void navigate('/tags/new')}>
          New tag
        </Button>
      </div>

      {tags.length === 0 ? (
        <EmptyState
          title="No tags yet"
          body="Tags mark what was going on around an entry — busy, unwell, travelling. Most get made while logging, in the moment — but you can start one here too."
          action={
            <Button variant="primary" onClick={() => void navigate('/tags/new')}>
              New tag
            </Button>
          }
        />
      ) : (
        <>
          <p className={styles['subtitle']}>
            Tap a tag to see everything it is on. Renaming keeps every entry, and deleting
            one removes the label without touching what was recorded.
          </p>

          {duplicates.length > 0 && (
            <p className={styles['duplicates']} data-testid="near-duplicates">
              {duplicates.map((pair) => `“${pair.a}” and “${pair.b}”`).join(', ')} look
              similar. Merge them if they are the same thing.
            </p>
          )}

          <div className={styles['list']}>
            {tags.map((tag) => {
              const used = counts.get(tag.id) ?? 0
              return (
                <div key={tag.id} className={styles['row']}>
                  <input
                    type="color"
                    className={styles['colour']}
                    value={tag.colour ?? '#8b939e'}
                    aria-label={`Colour for ${tag.name}`}
                    onChange={(event) => void recolour(tag, event.target.value)}
                  />
                  <span className={styles['body']}>
                    <input
                      className={styles['name']}
                      defaultValue={tag.name}
                      aria-label={`Name of ${tag.name}`}
                      // On blur rather than per keystroke: renaming to a name
                      // that momentarily clashes with another tag would fail
                      // halfway through typing it.
                      onBlur={(event) => void rename(tag, event.target.value)}
                    />
                    {/*
                      Offered even at zero. A tag used on nothing still opens,
                      onto the filtered empty state — which says so, where a
                      missing button says nothing at all.
                    */}
                    <button
                      type="button"
                      className={styles['usage']}
                      aria-label={`Show entries tagged ${tag.name}`}
                      onClick={() => {
                        showEntries(tag)
                      }}
                    >
                      {usageLabel(used)}
                    </button>
                  </span>
                  <span className={styles['actions']}>
                    {tags.length > 1 && (
                      <IconButton
                        label={`Merge ${tag.name} into another tag`}
                        onClick={() => setMerging(tag)}
                      >
                        <Merge />
                      </IconButton>
                    )}
                    <IconButton
                      label={`Delete ${tag.name}`}
                      onClick={() => void remove(tag)}
                    >
                      <Bin />
                    </IconButton>
                  </span>
                </div>
              )
            })}
          </div>
        </>
      )}

      <Sheet
        open={merging !== null}
        title={`Merge ${merging?.name ?? ''}`}
        // A merge is confirmed rather than committed as you go, and Cancel is
        // already in the actions below — so no corner control.
        dismissLabel={null}
        onClose={() => {
          setMerging(null)
          setMergeInto('')
        }}
      >
        <div className={styles['merge']}>
          <p>
            Every entry tagged <strong>{merging?.name}</strong> will be tagged with the
            one you pick instead, and <strong>{merging?.name}</strong> will be deleted. No
            entries are removed.
          </p>
          <Select
            label="Merge into"
            value={mergeInto}
            onChange={(event) => setMergeInto(event.target.value)}
          >
            <option value="">Choose a tag</option>
            {tags
              .filter((tag) => tag.id !== merging?.id)
              .map((tag) => (
                <option key={tag.id} value={tag.id}>
                  {tag.name}
                </option>
              ))}
          </Select>

          {/*
            Which direction, and how much is at stake on each side, before it
            happens. A merge is not undoable, and "Merge snack" alone does not
            say which of the two names survives.
          */}
          {merging !== null && mergeTarget !== undefined && (
            <p className={styles['mergeSummary']} data-testid="merge-summary">
              <strong>{merging.name}</strong> (
              {usageLabel(counts.get(merging.id) ?? 0).toLowerCase()}) will become{' '}
              <strong>{mergeTarget.name}</strong> (
              {usageLabel(counts.get(mergeTarget.id) ?? 0).toLowerCase()}).
            </p>
          )}

          <div className={styles['mergeActions']}>
            <Button onClick={() => setMerging(null)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={mergeInto === ''}
              onClick={() => void confirmMerge()}
            >
              Merge
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  )
}

function Merge() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M5 3v4a4 4 0 0 0 4 4h6M15 3v4a4 4 0 0 1-4 4H9m6 0-3-3m3 3-3 3"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function Bin() {
  return (
    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
      <path
        d="M4 6h12M8 6V4h4v2M6 6l1 10h6l1-10"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
